import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import DBSelector from "../components/DBSelector";
import { apiClient } from "../api/apiClient";
import endpointMap from "../Helper/endpointMap";
import { buildPayload } from "../Helper/buildPayload";
import { getToken } from "../Helper/auth";
import { Helmet } from "react-helmet";

export default function MigratePage() {
  const [source, setSource] = useState("");
  const [target, setTarget] = useState("");

  const [sourceCollection, setSourceCollection] = useState("");
  const [sourceDbName, setSourceDbName] = useState("");
  const [sourceApiKey, setSourceApiKey] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [sourceEmbeddingField, setSourceEmbeddingField] = useState("");

  const [targetCollection, setTargetCollection] = useState("");
  const [targetDbName, setTargetDbName] = useState("");
  const [targetApiKey, setTargetApiKey] = useState("");
  const [targetUrl, setTargetUrl] = useState("");
  const [targetEmbeddingField, setTargetEmbeddingField] = useState("");

  const [namespace, setNamespace] = useState("");
  const [batchSize, setBatchSize] = useState("100");

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleTransfer = async () => {
    setResult(null);
    setError(null);

    if (!source || !target) {
      setError("Please select both source and target databases.");
      return;
    }

    const key = `${source}_${target}`;
    console.log("Looking for endpoint key:", key);

    const endpoint = endpointMap[key];

    if (!endpoint) {
      setError(`Invalid source/target combination: ${source} → ${target}. Available: ${Object.keys(endpointMap).join(', ')}`);
      return;
    }

    const payload = buildPayload({
      source, target,
      sourceCollection, sourceDbName, sourceApiKey, sourceUrl, sourceEmbeddingField,
      targetCollection, targetDbName, targetApiKey, targetUrl, targetEmbeddingField,
      namespace, batchSize,
    });

    console.log("Built payload:", payload);

    if (!payload) {
      setError("Failed to build request payload. Please check all required fields.");
      return;
    }

    const token = await getToken();
    if (!token) {
      setError("Session expired. Please login again.");
      return;
    }

    setLoading(true);

    try {
      const res = await apiClient.post(endpoint, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res && res.status) {
        console.log(res);
        setResult({
          status: res.status,
          message: res.message,
          job_id: res.job_id,
          vector_count: res.vector_count,
          amount: res.amount,
        });
      } else {
        console.error("Invalid response:", res);
        setError("Invalid response from server");
      }
    } catch (err) {
      console.error("Transfer error:", err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-bg min-h-screen text-text-primary flex flex-col">
      <Helmet>
        <title>Vector Database Migration Tool | Transfer Vectors Easily</title>
        <meta
          name="description"
          content="Use Vector Migration to securely transfer vectors between databases. Supports Pinecone, Milvus, Qdrant, ChromaDB, Weaviate, MongoDB, and PGVector with batch size control and namespace management."
        />
        <meta
          name="keywords"
          content="vector migration, database transfer, vector database, batch transfer, data migration tool, Pinecone, Milvus, Qdrant, ChromaDB, Weaviate, MongoDB, PGVector, PostgreSQL"
        />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Navbar />

      <main className="flex-1 py-12 px-4 mt-10 max-w-4xl mx-auto w-full">
        <h1 className="text-3xl mt-10 font-bold mb-6 text-center text-text-primary">
          Transfer Vectors Between Databases
        </h1>

        <section className="flex flex-col gap-10 w-full">
          <DBSelector
            title="Source Database"
            type={source}
            setType={setSource}
            dbName={sourceDbName}
            setDbName={setSourceDbName}
            collection={sourceCollection}
            setCollection={setSourceCollection}
            apiKey={sourceApiKey}
            setApiKey={setSourceApiKey}
            url={sourceUrl}
            setUrl={setSourceUrl}
            embeddingField={sourceEmbeddingField}
            setEmbeddingField={setSourceEmbeddingField}
            namespace={namespace}
            setNamespace={setNamespace}
            batchSize={batchSize}
            setBatchSize={setBatchSize}
            isTarget={false}
          />

          <DBSelector
            title="Target Database"
            type={target}
            setType={setTarget}
            dbName={targetDbName}
            setDbName={setTargetDbName}
            collection={targetCollection}
            setCollection={setTargetCollection}
            apiKey={targetApiKey}
            setApiKey={setTargetApiKey}
            url={targetUrl}
            setUrl={setTargetUrl}
            embeddingField={targetEmbeddingField}
            setEmbeddingField={setTargetEmbeddingField}
            namespace={namespace}
            setNamespace={setNamespace}
            batchSize={batchSize}
            setBatchSize={setBatchSize}
            isTarget={true}
          />

          <button
            onClick={handleTransfer}
            disabled={loading}
            className="bg-primary text-white px-6 py-3 rounded-md font-bold w-full max-w-sm mx-auto hover:bg-primary-soft transition disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Start vector migration process"
          >
            {loading ? "Checking Your Credentials..." : "Start Migration"}
          </button>

          {/* Status Panel */}
          <section
            className="bg-primary-dark border border-border p-4 rounded-md mt-6"
            aria-labelledby="migration-status"
          >
            <h2 id="migration-status" className="font-semibold mb-2 text-white">
              Status
            </h2>

            {error && <p className="text-red-400">{error}</p>}

            {!error && !result && (
              <p className="text-text-muted">No migration run yet.</p>
            )}

            {result && (
              <div className="relative">
                <p className="text-yellow-400 font-semibold mb-2">
                  {result.message}
                </p>

                <div className="bg-primary border border-border-strong p-4 rounded mb-4 text-sm text-white space-y-3">
                  <div className="flex justify-between">
                    <span className="text-text-muted">Status</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        result.status === "payment_required"
                          ? "bg-yellow-500/20 text-yellow-400"
                          : "bg-green-500/20 text-green-400"
                      }`}
                    >
                      {result.status.replace("_", " ").toUpperCase()}
                    </span>
                  </div>

                  <div>
                    <p className="text-text-muted">Message</p>
                    <p className="font-medium text-white">{result.message}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-primary-dark p-2 rounded">
                      <p className="text-text-muted text-xs">Job ID</p>
                      <p className="font-mono text-xs truncate text-white">
                        {result.job_id}
                      </p>
                    </div>

                    <div className="bg-primary-dark p-2 rounded">
                      <p className="text-text-muted text-xs">Vectors</p>
                      <p className="font-semibold text-white">{result.vector_count}</p>
                    </div>

                    <div className="bg-primary-dark p-2 rounded">
                      <p className="text-text-muted text-xs">Amount</p>
                      <p className="font-semibold text-white">{result.amount}</p>
                    </div>
                  </div>
                </div>

                {result.status === "payment_required" && (
                  <div className="flex justify-end">
                    <button
                      onClick={async () => {
                        try {
                          const token = await getToken();
                          if (!token) throw new Error("User not authenticated");

                          const payRes = await apiClient.post(
                            "/create-payment-intent",
                            { job_id: result.job_id, amount: result.amount },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );

                          if (payRes.checkout_url) {
                            window.location.assign(payRes.checkout_url);
                          } else {
                            throw new Error("Payment URL not returned from server.");
                          }
                        } catch (err) {
                          console.error("Payment error:", err);
                          setError(
                            err.response?.data?.message || err.message || "Payment failed"
                          );
                        }
                      }}
                      className="px-3 py-1.5 rounded-md text-sm font-semibold bg-yellow-400 hover:bg-yellow-500 text-black transition"
                    >
                      Proceed To Payment
                    </button>
                  </div>
                )}
              </div>
            )}
          </section>
        </section>
      </main>

      <Footer />
    </div>
  );
}